import _ from 'lodash';
import { IconButton } from '../../../components/common/IconButton';
import TextSearchInput from '../../../components/common/TextSearchInput';
import { useState } from 'react';
import MultiButtonSwitch from '../../../components/common/editors/MultiButtonSwitch';
import { Icon } from '../../../components/common/Icon';
import { FactoidImage } from './FactoidImage';
import HighlightedText from '../../../components/common/HighlightedText';
import { sortObjectKeysDeep } from '../../../../../model/cerokm/cerokmUtils.mjs';


const filterOptions = [
  { label: <span className='p-1 px-4'>All</span>, value: '' },
  { label: <span className='bg-light-warning p-1 rounded'>Differences</span>, value: 'differences' },
  { label: <span className='bg-light-partial-success p-1 rounded no-wrap'>Missing in one</span>, value: 'missing' },
  { label: <span className='bg-light-white p-1 rounded'>Coincidences</span>, value: 'coincidences' },
  { label: <Icon icon="code_off" level="danger" title='Invalid schema path or value'/>, value: 'invalidPath' },
  { label: <Icon icon="flaky" level="danger"/>, value: 'contradiction' },
  { label: <Icon icon="flaky" level="info"/>, value: 'noConsensus' },
  { label: <Icon icon="search_off" level="warning"/>, value: 'missingSpec' },
  { label: <Icon icon="gpp_bad" level="danger" title='Required value missing'/>, value: 'requiredMissing' },
  { label: <Icon icon="functions" level="danger" title='Rule execution errors'/>, value: 'ruleError' },
  { label: <Icon icon="functions" level="info" title='Rule generated values'/>, value: 'ruleGenerated' },
];

export function SpecsJsonTable({ specJsons, titles, warnings, onRefresh, onSave, onApplyTransformations, onCellClick }) {
  const [textFilter, setTextFilter] = useState('');
  const [filter, setFilter] = useState('');

  const renderValue = (value, {fullPath, parentPath, textFilter}) => {
    if(parentPath === 'images.spec' && value?.uri) {
      return <FactoidImage factoid={{derivedSpecs: [[fullPath, value]]}} />
    }

    if (_.isBoolean(value)) {
      return <span className="text-success">{value ? '✅' : '⛔'}</span>;
    } else if (_.isObject(value) && value.value !== undefined && value.unit !== undefined) {
      return <span>
        <span className='text-info'>
          <HighlightedText text={value.value.toString()} regex={textFilter} />
          </span> <span className="text-info">
            <HighlightedText text={value.unit.toString()} regex={textFilter} />
          </span>
      </span>;
    } else if (_.isString(value)) {
      return <span className='text-violeta'>"<HighlightedText text={value.toString()} regex={textFilter} />"</span>;
    } else if (_.isNull(value)) {
    } else if (_.isNumber(value)) {
      return <span className='text-primary'><HighlightedText text={value.toString()} regex={textFilter} /></span>;
    } else if (_.isNull(value)) {
      return <span className="text-muted">null</span>;
    } else if (_.isEmpty(value)) {
      return '-';
    } else {
      return JSON.stringify(value);
    }
  };

  const colSpan = specJsons.length + 1;
  const visibleParentPaths = {};

  const parentsBlacklist = ['images.spec'];

  const buildWarningIcons = (warnings) => {
    if (!warnings?.length) return null;

    const style = { position: 'absolute', top: 0, left: 0, zoom: 0.7 };
    if (_.some(warnings, w => w.type === 'contradiction')) {
      return <IconButton level='danger' style={style} icon='flaky' title='Contradiction: Two factoids with contradicting specs' />;
    } else if (_.some(warnings, w => w.type === 'invalidValue')) {
      return <IconButton level='danger' style={style} icon='code_off' title='Invalid value' />;
    } else if (_.some(warnings, w => w.type === 'requiredMissing')) {
      return <IconButton level='danger' style={style} icon='gpp_bad' title='Required value missing' >Required</IconButton>;
    } else if (_.some(warnings, w => w.type === 'ruleError')) {
      const message = _.find(warnings, w => w.type === 'ruleError').message || 'Rule error';
      return <IconButton level='danger' style={style} icon='functions' title={message} />;
    } else if (_.some(warnings, w => w.type === 'missingSpec')) {
      return <IconButton level='warning' style={style} icon='search_off' title='Missing spec in the factoid of that spec' />;
    } else if (_.some(warnings, w => w.type === 'ruleGenerated')) {
      return <IconButton level='info' style={style} icon='functions' title='Rule generated'/>;
    } else if (_.some(warnings, w => w.type === 'noConsensus')) {
      return <IconButton level='info' style={style} icon='flaky' title='No consensus, spec missing in some factoids of the fragment' />;
    } else if (_.some(warnings, w => w.type === 'duplicate')) {
      // return <IconButton level='secondary' style={style} icon='content_copy' title='Duplicate' />;
    }
    return null;
  };


  const renderRows = (path, value, level) => {
    let margin = { paddingLeft: 10+level * 30 };

    return Object.entries(value).map(([key, value]) => {
      let parentPath = path.join('.');
      let fullPath = [...path, key].join('.');

      /** @type {any} */
      let hide = false;

      const rowWarnings = _.flatMap(warnings, ws => _.filter(ws, w => w.path === fullPath));
      const rowSubvalueWarnings = _.flatMap(warnings, ws => _.filter(ws, w => w.path === fullPath + '.value'));

      const notices = {};
      for(const noticeType of ['missingSpec', 'invalidPath', 'noConsensus', 'invalidValue', 'contradiction', 'requiredMissing', 'ruleError', 'ruleGenerated']) {
        notices[noticeType] = _.some(rowWarnings.concat(rowSubvalueWarnings), w => w.type === noticeType);
      }

      const isInvalidPath = _.some(rowWarnings, w => w.type === 'invalidPath');

      const values = specJsons.map(json => _.get(json, fullPath));

      const parentPathMatchesFilter = textFilter && parentPath.toLowerCase().includes(textFilter.toLowerCase());
      const pathMatchesFilter = textFilter && fullPath.toLowerCase().includes(textFilter.toLowerCase());
      const valuesMatchesFilter = textFilter && JSON.stringify(values).toLowerCase().includes(textFilter.toLowerCase());
      const isFirstMatchingPath = pathMatchesFilter && !parentPathMatchesFilter;

      // When filtering rows, make a row with the whole parent path
      let optionalParentPathRow = <tr key={fullPath + 'opt'} className={`${!isInvalidPath ? ' bg-light-secondary' : 'bg-danger text-white'}`} style={{ borderTop: 'solid 5px white' }} onClick={() => onCellClick(parentPath)}>
        <td className="mr-2 clickable " colSpan={colSpan} style={{ paddingLeft: '10px' }}>{parentPath}:</td>
      </tr>;

      if (visibleParentPaths[parentPath] || !parentPath) {
        optionalParentPathRow = null;
      }

      hide = !pathMatchesFilter && (textFilter || filter);

      if (_.isPlainObject(value) && value.value === undefined && !parentsBlacklist.includes(parentPath)) {
        // Key row
        if(!isFirstMatchingPath) {
          optionalParentPathRow = null;
        } else {
          visibleParentPaths[parentPath] = visibleParentPaths[parentPath] || !!optionalParentPathRow;
        }
        if(!hide) {
          visibleParentPaths[fullPath] = true;
        }

        const allHaveIt = _.every(values, v => v !== undefined);

        let rowClass = `${isInvalidPath ? 'bg-danger text-white' : (allHaveIt ? 'bg-light' : 'bg-light-partial-success')} ${hide ? 'd-none' : ''}`;

        let icons;
        if(notices.requiredMissing) {
          rowClass += ' bg-light-danger text-danger';
          icons = buildWarningIcons(rowWarnings.concat(rowSubvalueWarnings));

          return [
            optionalParentPathRow,

            <tr key={fullPath} className={rowClass}>
              <td className="mr-2 clickable" style={margin} onClick={() => onCellClick(fullPath)}>
                <HighlightedText text={key + ':'} regex={textFilter} />
              </td>
              {specJsons.map((json, i) => {
                const warns = _.filter(warnings[i], ws => ws.path === fullPath).concat(_.filter(warnings[i], ws => ws.path === fullPath + '.value'));
                const warnIcons = buildWarningIcons(warns);
                let valueClasses = '';
                if(_.some(warns, w => w.type === 'invalidValue' || w.type === 'requiredMissing')) {
                  valueClasses += ' bg-light-danger text-danger';
                } else if(value === undefined) {
                  valueClasses += ' bg-light-secondary'
                }

                return <td key={i} className={`text-center position-relative ${valueClasses}`}>{warnIcons}</td>;
              })}
            </tr>,

            renderRows([...path, key], value, level + 1)
          ];
        } else {
            return [
              optionalParentPathRow,

              <tr key={fullPath} className={rowClass}>
                <td className="mr-2 clickable" colSpan={colSpan} style={margin} onClick={() => onCellClick(fullPath)}>
                  <HighlightedText text={key + ':'} regex={textFilter} />
                </td>
              </tr>,

              renderRows([...path, key], value, level + 1)
            ];
        }

      } else if (_.isArray(value)) {
        visibleParentPaths[fullPath] = !hide;
        if(!valuesMatchesFilter) {
          optionalParentPathRow = null;
        } else {
          visibleParentPaths[parentPath] = visibleParentPaths[parentPath] || !!optionalParentPathRow;
        }
        // Key row
        return [
          optionalParentPathRow,
          <tr key={fullPath} className={`${isInvalidPath ? 'bg-danger text-white' : 'bg-light'} ${hide ? 'd-none' : ''}`}>
            <td className="mr-2 clickable" colSpan={colSpan} style={margin} onClick={() => onCellClick(fullPath)}>
              <HighlightedText text={key + ':'} regex={textFilter} />
            </td>
          </tr>,
          value.map((v, i) => renderRows([...path, key], { [i]: v }, level + 1))
        ];
      } else {
        const uniqueValues = _.uniq(values.map(JSON.stringify));
        const allSameValue = uniqueValues.length === 1;
        const oneDifference = uniqueValues.filter(v => v !== undefined).length > 1;
        const someIsMissing = values.some(v => v == undefined);

        hide = false;
        if (textFilter) {
          hide = !pathMatchesFilter && !valuesMatchesFilter;
        }

        if (!hide && filter) {
          switch (filter) {
            case 'differences':
              hide = allSameValue || (someIsMissing && !oneDifference);
              break;
            case 'coincidences':
              hide = !allSameValue;
              break;
            case 'missing':
              hide = allSameValue;
              break;
            case 'invalidPath':
              hide = !notices.invalidPath && !notices.invalidValue;
              break;
            case 'contradiction':
              hide = !notices.contradiction;
              break;
            case 'noConsensus':
              hide = !notices.noConsensus;
              break;
            case 'missingSpec':
              hide = !notices.missingSpec;
              break;
            case 'requiredMissing':
              hide = !notices.requiredMissing;
              break;
            case 'ruleError':
              hide = !notices.ruleError;
              break;
            case 'ruleGenerated':
              hide = !notices.ruleGenerated;
              break;
          }
        }

        if(hide) {
          optionalParentPathRow = null;
        } else if(optionalParentPathRow) {
          visibleParentPaths[parentPath] = true;
        }

        let rowClass = `${hide ? 'd-none' : ''} ${isInvalidPath ? 'bg-light-danger text-danger' : ''}`;

        const specPathColumn = <td className={`mr-2 clickable ${isInvalidPath ? 'bg-danger text-white' : ''}`} style={margin} onClick={() => onCellClick(fullPath)}>
          <HighlightedText text={key + ':'} regex={textFilter} />
        </td>;

        if (allSameValue && !notices.contradiction) {
          const onClick = () => onCellClick(fullPath, undefined, value);

          let warnIcons = buildWarningIcons(rowWarnings.concat(rowSubvalueWarnings));

          let valueClasses = '';
          if(notices.invalidValue || notices.requiredMissing) {
            valueClasses += ' bg-light-danger text-danger';
          }

          return [
            optionalParentPathRow,
            <tr key={fullPath} className={rowClass}>
             {specPathColumn}
              <td colSpan={colSpan} className={`text-center clickable position-relative ${valueClasses}`} onClick={onClick}>
                {warnIcons}{renderValue(_.get(specJsons[0], fullPath), {fullPath, parentPath, textFilter})}
              </td>
            </tr>
          ];
        } else {
          rowClass += (someIsMissing && !oneDifference) ? ' bg-light-partial-success' : ' bg-light-warning';

          return [
            optionalParentPathRow,
            <tr key={fullPath} className={rowClass}>
              {specPathColumn}
              {specJsons.map((json, i) => {
                const value = _.get(json, fullPath);
                const onClick = () => onCellClick(fullPath, i, value);

                const warns = _.filter(warnings[i], ws => ws.path === fullPath).concat(_.filter(warnings[i], ws => ws.path === fullPath + '.value'));
                const warnIcons = buildWarningIcons(warns);
                let valueClasses = '';
                if(_.some(warns, w => w.type === 'invalidValue' || w.type === 'requiredMissing')) {
                  valueClasses += ' bg-light-danger text-danger';
                } else if(value === undefined) {
                  valueClasses += ' bg-light-secondary'
                }

                return <td key={i} className={`text-center clickable position-relative ${valueClasses}`} onClick={onClick}>{warnIcons}{renderValue(value, {fullPath, parentPath, textFilter})}</td>;
              })}
            </tr>
          ];
        }
      }
    });
  };

  let specs = sortObjectKeysDeep(_.merge({}, ...specJsons));

  return <table className="specs-json-table-comparison table table-sm monospace" style={{ fontSize: '13px', width: 'inherit' }}>
    <thead className='sticky-top bg-dark text-white'>
      <tr>
        <th colSpan={colSpan}>
          <div className='d-flex align-items-center'>
            <MultiButtonSwitch options={filterOptions} value={filter} onChange={setFilter} />
            <TextSearchInput className={'form-control-sm ml-2 self-grow-1'} value={textFilter}
                               onChange={setTextFilter} placeholder={'Filter...'}/>
          </div>
        </th>
      </tr>
      <tr>
        <th className='align-middle text-center'>
          Specs
          <IconButton level='primary' icon='refresh' onClick={onRefresh} />
          <IconButton level='success' icon='save' onClick={onSave} title='Save on merged specs collection'/>
          <IconButton level='info' icon='functions' onClick={onApplyTransformations} title='Apply transformations and validations'/>
        </th>
        {specJsons.map((specJson, i) => <th key={i} style={{ width: '300px' }} className='text-center align-top'>{titles[i]}</th>)}
      </tr>
    </thead>
    <tbody style={{ fontSize: '12px'}}>
      {renderRows([], specs, 0)}
    </tbody>
  </table>;
}
